/**
 * PostHog browser client wrapper.
 *
 * Reads REACT_APP_POSTHOG_KEY / REACT_APP_POSTHOG_HOST at build time.
 * All exports are no-ops when key is missing so dev builds stay quiet.
 *
 * Session replay is OFF by default (set REACT_APP_POSTHOG_REPLAY=1 to enable).
 */

import posthog, { PostHog } from 'posthog-js';

let initialised = false;
let active: PostHog | null = null;

const KEY = process.env.REACT_APP_POSTHOG_KEY;
const HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';
const REPLAY = process.env.REACT_APP_POSTHOG_REPLAY === '1';

export function initPostHog(): PostHog | null {
  if (initialised) return active;
  initialised = true;

  if (!KEY) return null;

  try {
    posthog.init(KEY, {
      api_host: HOST,
      capture_pageview: true,
      capture_pageleave: true,
      autocapture: true,
      capture_exceptions: true,
      disable_session_recording: !REPLAY,
      persistence: 'localStorage+cookie',
      loaded: (ph) => {
        if (process.env.NODE_ENV !== 'production') {
          ph.debug(false);
        }
      },
    });
    active = posthog;
    return posthog;
  } catch {
    return null;
  }
}

export function isEnabled(): boolean {
  return active !== null;
}

export function identify(distinctId: string, properties?: Record<string, unknown>): void {
  if (!active) return;
  try {
    active.identify(distinctId, properties);
  } catch {
    /* swallow */
  }
}

export function reset(): void {
  if (!active) return;
  try {
    active.reset();
  } catch {
    /* swallow */
  }
}

export function captureException(error: unknown, properties?: Record<string, unknown>): void {
  if (!active) return;
  try {
    if (error instanceof Error) {
      active.captureException(error, properties);
    } else {
      active.capture('$exception', {
        $exception_type: 'NonError',
        $exception_message: String(error),
        ...properties,
      });
    }
  } catch {
    /* swallow */
  }
}

export function capture(event: string, properties?: Record<string, unknown>): void {
  if (!active) return;
  try {
    active.capture(event, properties);
  } catch {
    /* swallow */
  }
}
